import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { useLazySearchWithAiQuery } from 'services/contactsV2';
import { useContactsSearchParam } from 'pages/contactsExtended/hooks/useContactsSearchParam';
import { reverseParsedFilters } from 'services/contacts/filters';
import { defaultFilters } from 'components/organisms/contactsFilters/defaultFilters';
import { trackEvent } from 'helpers/analytics';
import { activeNetworkSelector } from 'redux/selectors';

// Connection filters aren't in the model's schema, so don't suggest them here.
export const FAILURE_MESSAGES = {
  not_a_search: {
    message: "That doesn't look like a contact search.",
    hint: 'Describe the people you want to find — their role, seniority, experience, skills, industry, location, or company. For example "senior product managers in New York with 5+ years of experience".',
  },
  ambiguous: {
    message: "That could mean a few different things, so we didn't want to guess.",
    hint: 'Add a detail or two, like a role, a seniority, a location, or an industry. For example "engineering managers at fintech companies in Europe".',
  },
  unsupported_concept: {
    message: "We understood the question, but we can't filter on that yet.",
    hint: 'The search can match on role, seniority, years of experience, skills, industry, location, company, company size, funding stage, tags, and job-seeking status. Anything else is worth setting in the filters below.',
  },
  internal_error: {
    message: 'Something went wrong running that search.',
    hint: 'Please try again in a moment, or set the filters below.',
  },
};

const DEFAULT_FAILURE = FAILURE_MESSAGES.not_a_search;

// Filtering the raw string rather than rebuilding through URLSearchParams keeps the other params
// byte-for-byte, including bracketed filter keys.
const searchWithout = (keys) =>
  window.location.search
    .replace(/^\?/, '')
    .split('&')
    .filter((param) => param && !keys.includes(param.split('=')[0]))
    .join('&');

export const useAiFilters = ({ setFilters }) => {
  const [value, setValue] = useState(new URLSearchParams(window.location.search).get('aiFilters') || '');
  const [showInput, setShowInput] = useState(!!value);
  const [failure, setFailure] = useState(null);
  const [searchWithAi, { isFetching }] = useLazySearchWithAiQuery();
  const history = useHistory();
  const [queryParams] = useContactsSearchParam();
  const { id: collectionId } = useSelector(activeNetworkSelector);
  const inputRef = useRef(null);

  const reset = () => setFailure(null);

  const onEnter = async () => {
    if (!value) {
      setValue('');
      reset();
      history.push({ search: '' });
      setFilters(defaultFilters);
      return;
    }

    reset();
    const search = searchWithout(['aiFilters', 'page']);

    history.push({ search: `${search}${search ? '&' : '?'}page=1&aiFilters=${encodeURIComponent(value)}` });

    trackEvent('contacts:natural_language_query', {
      query: value,
      collectionId,
    });

    const { data, error } = await searchWithAi({ query: value, queryParams });

    if (error) {
      // An outage, not a misunderstood prompt.
      setFailure(FAILURE_MESSAGES.internal_error);
      return;
    }

    if (!Object.keys(data?.filters || {}).length) {
      setFailure(FAILURE_MESSAGES[data?.unsupportedReason] || DEFAULT_FAILURE);
      return;
    }

    setFilters(reverseParsedFilters(data.filters), { resetPreviousFilters: true });
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.selectionStart = inputRef.current.value.length;
      inputRef.current.selectionEnd = inputRef.current.value.length;
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }

    const handleResetFilters = () => {
      setValue('');
      reset();
      // Strip only our own params: onResetFilters has already written the URL it wants, sort
      // included, and replace keeps Clear all to a single history entry.
      history.replace({ search: searchWithout(['aiFilters', 'aiFilterLoading']) });
    };

    // contactsSidebar dispatches a non-bubbling Event on document, so window never sees it.
    document.addEventListener('resetFilters', handleResetFilters);
    return () => document.removeEventListener('resetFilters', handleResetFilters);
  }, [history]);

  return { value, setValue, showInput, setShowInput, failure, isFetching, onEnter, inputRef };
};
